// apps/client/src/state/slices/decorate.ts (M1-T16) — keys start with "decorate" (§8.7). Decorate mode's UI state, plus the pure
// helpers the screen uses: the furniture.* request for a set-down piece, the client mirror of the server's room check, and the tray.
import { getEntry } from '@whiskerton/content';
import type { ActionReq, ItemView, RoomView, SpaceId } from '@whiskerton/shared';
import { checkPlacement, type Placed } from '@whiskerton/shared/rules/placement';
import type { Held } from '../../world/decorate';
import type { SliceCreator } from '../types';

export interface DecorateSlice {
  decorateActive: boolean; decorateHeld: Held | null; decorateValid: boolean; decorateTab: 'furniture' | 'rugs' | 'wallpaper' | 'floor';
  decorateSet(p: Partial<Pick<DecorateSlice, 'decorateActive' | 'decorateHeld' | 'decorateValid' | 'decorateTab'>>): void;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends DecorateSlice {} }
export const slice: SliceCreator<DecorateSlice> = (set) => ({
  decorateActive: false, decorateHeld: null, decorateValid: true, decorateTab: 'furniture',
  decorateSet: (p) => set(p),
});

/** A piece lifted from pockets or storage is placed; one lifted from the floor (or the yard) is moved. */
export function requestFor(h: Held, space: SpaceId):
  | { type: 'furniture.place'; req: ActionReq<'furniture.place'> } | { type: 'furniture.move'; req: ActionReq<'furniture.move'> } {
  return h.from === null
    ? { type: 'furniture.place', req: { item: h.item.id, space, at: { x: h.x, y: h.y }, rot: h.rot } }
    : { type: 'furniture.move', req: { item: h.item.id, at: { x: h.x, y: h.y }, rot: h.rot } };
}
/** Mirrors the server (M1-T11): every exit tile stays clear, not only the front door. */
export function surfaceCheck(view: RoomView, placed: readonly Placed[]): (cand: Placed) => string | null {
  const doors = view.exits.length ? view.exits.map((e) => e.at) : [view.door];
  return (cand) => {
    for (const door of doors) { const c = checkPlacement({ w: view.w, h: view.h, door }, placed, cand); if (c) return c; }
    return null;
  };
}
/** Mirrors M1-T11, so the tray never offers a move the server rejects: storage is reachable only inside your own house, so the
 *  yard takes pocket items only; fish (tanks) go in rooms only; `outdoor: true` furniture only in the yard. `rugs` = category
 *  'rug', `furniture` = every other category (+ fish); the wallpaper/floor tabs list those pocket items, in rooms only. */
export function trayItems(items: readonly ItemView[], where: 'room' | 'yard', tab: DecorateSlice['decorateTab']): ItemView[] {
  return items.filter((i) => {
    if (!(i.at.loc === 'pockets' || (where === 'room' && i.at.loc === 'storage'))) return false;
    const e = getEntry(i.catalogId);
    if (tab === 'wallpaper' || tab === 'floor') return where === 'room' && i.at.loc === 'pockets' && e?.kind === tab;
    if (e?.kind === 'fish') return where === 'room' && tab === 'furniture';
    if (e?.kind !== 'furniture' || (where === 'yard' && !e.outdoor)) return false;
    return tab === 'rugs' ? e.category === 'rug' : e.category !== 'rug';
  });
}
